// calculator.js - FIXED VERSION with proper event coordination
import { langManager } from '../lang/langManager.js';
import { log } from '../utils/logging.js';
import { resetTurnstile } from '../utils/turnstile.js';
import {
  submitOrderForm,
  showFormStatus,
  validateSharedFields,
  SHARED_LIMITS
} from '../utils/formSubmit.js';

// =========================
// CONSTANTS & LIMITS
// =========================
const LIMITS = {
  ...SHARED_LIMITS,
  GALETTES_MAX: 1000,
  CREPES_MAX: 1000,
  DURATION_MAX: 12,
  DURATION_MIN: 2
};

// =========================
// CALCULATOR
// =========================
function calculate() {
  log('[Calculator] === Calculate called ===');

  const costsSection = document.getElementById('costs-section');
  if (!costsSection) {
    console.error('[Calculator] Costs section not found!');
    return;
  }

  const galettesEl = costsSection.querySelector('#galettes');
  const crepesEl = costsSection.querySelector('#crepes');
  const durationEl = costsSection.querySelector('#duration');

  if (!galettesEl || !crepesEl || !durationEl) {
    console.error('[Calculator] Input elements not found');
    return;
  }

  const galettes = parseInt(galettesEl.value) || 0;
  const crepes = parseInt(crepesEl.value) || 0;
  const duration = parseInt(durationEl.value) || 2;

  const totalGalettes = galettes * 2.5;
  const totalCrepes = crepes * 1.0;
  const totalBase = 450;

  const totalItems = galettes + crepes;
  const portionsPerHour = duration > 0 ? totalItems / duration : 0;

  let staffNeeded = 0;
  let isOverLimit = false;

  if (portionsPerHour > 100) {
    staffNeeded = langManager.getText('request') || 'N/A';
    isOverLimit = true;
  } else if (portionsPerHour > 70) staffNeeded = 3;
  else if (portionsPerHour > 40) staffNeeded = 2;
  else if (portionsPerHour > 0) staffNeeded = 1;

  const staffEl = costsSection.querySelector('#staff');

  if (staffEl) {
    if (isOverLimit) {
      staffEl.value = langManager.getText('na') || 'N/A';
      staffEl.classList.add('na');
    } else {
      staffEl.value = staffNeeded;
      staffEl.classList.remove('na');
    }
  }

  const totalStaff = typeof staffNeeded === 'number' ? staffNeeded * 50 * duration : 0;

  const totalGalettesEl = costsSection.querySelector('#total-galettes');
  const totalCrepesEl = costsSection.querySelector('#total-crepes');
  const totalBaseEl = costsSection.querySelector('#total-base');
  const staffTotalElement = costsSection.querySelector('#total-staff');

  if (!totalGalettesEl || !totalCrepesEl || !totalBaseEl || !staffTotalElement) {
    console.error('[Calculator] Some total elements are missing!');
    return;
  }

  totalGalettesEl.textContent = `€${totalGalettes.toFixed(2)}`;
  totalCrepesEl.textContent = `€${totalCrepes.toFixed(2)}`;
  totalBaseEl.textContent = `€${totalBase.toFixed(2)}`;

  if (isOverLimit) {
    staffTotalElement.textContent = langManager.getText('na') || 'N/A';
    staffTotalElement.classList.add('na');
  } else {
    staffTotalElement.textContent = `€${totalStaff.toFixed(2)}`;
    staffTotalElement.classList.remove('na');
  }

  if (isOverLimit) {
    ['total-net', 'total-vat', 'total-gross'].forEach(id => {
      const el = costsSection.querySelector(`#${id}`);
      if (el) {
        el.textContent = langManager.getText('na') || 'N/A';
        el.style.color = 'var(--brand)';
      }
    });
    const note = document.getElementById('over-limit-note');
    if (note) note.style.display = 'block';
  } else {
    let net = totalBase + totalStaff + totalCrepes + totalGalettes;
    if (net < 750) net = 750;

    const vat = net * 0.07;
    const gross = net + vat;

    const totalNetEl = costsSection.querySelector('#total-net');
    const totalVatEl = costsSection.querySelector('#total-vat');
    const totalGrossEl = costsSection.querySelector('#total-gross');

    if (totalNetEl) {
      totalNetEl.textContent = `€${net.toFixed(2)}`;
      totalNetEl.style.color = '';
    }

    if (totalVatEl) {
      totalVatEl.textContent = `€${vat.toFixed(2)}`;
      totalVatEl.style.color = '';
    }

    if (totalGrossEl) {
      totalGrossEl.textContent = `€${gross.toFixed(2)}`;
      totalGrossEl.style.color = '';
    }

    const note = document.getElementById('over-limit-note');
    if (note) note.style.display = 'none';
  }

  log('[Calculator] === Calculate finished ===');
}

// =========================
// GET BOOKING DETAILS
// =========================
function getBookingDetails() {
  const eventDate = document.getElementById('event-date').value;
  const galettes = parseInt(document.getElementById('galettes').value) || 0;
  const crepes = parseInt(document.getElementById('crepes').value) || 0;
  const duration = parseInt(document.getElementById('duration').value) || 2;

  const totalGalettes = galettes * 2.5;
  const totalCrepes = crepes * 1.0;
  const totalBase = 450;

  const totalItems = galettes + crepes;
  const portionsPerHour = duration > 0 ? totalItems / duration : 0;
  const isOverLimit = portionsPerHour > 100;

  let staffNeeded = 0;
  if (!isOverLimit) {
    if (portionsPerHour > 70) staffNeeded = 3;
    else if (portionsPerHour > 40) staffNeeded = 2;
    else if (portionsPerHour > 0) staffNeeded = 1;
  }

  const totalStaff = staffNeeded * 50 * duration;

  let net = totalBase + totalStaff + totalCrepes + totalGalettes;
  if (net < 750) net = 750;

  const vat = net * 0.07;
  const gross = net + vat;

  const spaceNeeded = 3 + Math.max(0, staffNeeded - 1);
  const socketsNeeded = 2 + Math.max(0, staffNeeded - 1);

  return {
    eventDate,
    galettes,
    crepes,
    duration,
    staffNeeded,
    totalGalettes,
    totalCrepes,
    totalBase,
    totalStaff,
    isOverLimit,
    net,
    vat,
    gross,
    spaceNeeded,
    socketsNeeded
  };
}

// =========================
// TURNSTILE CALLBACK
// =========================
window.onTurnstileSuccess = function (token) {
  document.getElementById('turnstile_token').value = token;
  log('[Calculator] Turnstile token received:', token);
};

// =========================
// INPUT VALIDATION
// =========================
function enforceInputLimits() {
  log('[Calculator] Enforcing input limits...');

  const messageField = document.getElementById('contact-message');
  if (messageField) {
    let counter = messageField.parentElement.querySelector('.char-counter');
    if (!counter) {
      counter = document.createElement('div');
      counter.className = 'char-counter';
      counter.style.cssText = 'font-size: 0.85em; color: #666; margin-top: 4px; text-align: right;';
      messageField.parentElement.appendChild(counter);
    }

    const updateCounter = () => {
      const length = messageField.value.length;
      const remaining = LIMITS.MESSAGE_MAX_LENGTH - length;
      counter.textContent = `${length} / ${LIMITS.MESSAGE_MAX_LENGTH} characters`;

      if (remaining < 50) {
        counter.style.color = remaining < 0 ? '#e90501' : '#ff8800';
      } else {
        counter.style.color = '#666';
      }

      if (length > LIMITS.MESSAGE_MAX_LENGTH) {
        messageField.value = messageField.value.substring(0, LIMITS.MESSAGE_MAX_LENGTH);
        counter.textContent = `${LIMITS.MESSAGE_MAX_LENGTH} / ${LIMITS.MESSAGE_MAX_LENGTH} characters (max reached)`;
      }
    };

    messageField.addEventListener('input', updateCounter);
    updateCounter();
  }

  const nameField = document.getElementById('contact-name');
  if (nameField) {
    nameField.setAttribute('maxlength', LIMITS.NAME_MAX_LENGTH);
  }

  const emailField = document.getElementById('contact-email');
  if (emailField) {
    emailField.setAttribute('maxlength', LIMITS.EMAIL_MAX_LENGTH);
  }

  const phoneField = document.getElementById('contact-phone');
  if (phoneField) {
    phoneField.setAttribute('maxlength', LIMITS.PHONE_MAX_LENGTH);
    phoneField.addEventListener('input', e => {
      e.target.value = e.target.value.replace(/[^0-9+\-() ]/g, '');
    });
  }

  const galettesField = document.getElementById('galettes');
  if (galettesField) {
    galettesField.setAttribute('max', LIMITS.GALETTES_MAX);
    galettesField.addEventListener('input', e => {
      if (parseInt(e.target.value) > LIMITS.GALETTES_MAX) {
        e.target.value = LIMITS.GALETTES_MAX;
      }
    });
  }

  const crepesField = document.getElementById('crepes');
  if (crepesField) {
    crepesField.setAttribute('max', LIMITS.CREPES_MAX);
    crepesField.addEventListener('input', e => {
      if (parseInt(e.target.value) > LIMITS.CREPES_MAX) {
        e.target.value = LIMITS.CREPES_MAX;
      }
    });
  }

  const durationField = document.getElementById('duration');
  if (durationField) {
    durationField.setAttribute('min', LIMITS.DURATION_MIN);
    durationField.setAttribute('max', LIMITS.DURATION_MAX);
    durationField.addEventListener('input', e => {
      const val = parseInt(e.target.value);
      if (val < LIMITS.DURATION_MIN) e.target.value = LIMITS.DURATION_MIN;
      if (val > LIMITS.DURATION_MAX) e.target.value = LIMITS.DURATION_MAX;
    });
  }
}

// =========================
// EMAIL SUBMISSION HANDLER
// =========================
async function handleSubmit() {
  const submitBtn = document.getElementById('submit-booking');
  const statusId = 'form-status';

  const name = document.getElementById('contact-name').value.trim();
  const email = document.getElementById('contact-email').value.trim();
  const phone = document.getElementById('contact-phone').value.trim();
  const message = document.getElementById('contact-message').value.trim();
  const token = document.getElementById('turnstile_token').value;

  const sharedError = validateSharedFields({ name, email, phone, message }, langManager.getText);
  if (sharedError) {
    showFormStatus({ submitBtn, statusId, message: sharedError, isError: true });
    return;
  }

  if (!token) {
    showFormStatus({
      submitBtn,
      statusId,
      message: 'Please complete the captcha verification',
      isError: true
    });
    return;
  }

  const booking = getBookingDetails();

  await submitOrderForm({
    submitBtn,
    statusId,
    sendingLabel: 'Sending...',
    idleLabel: langManager.getText('sendRequestBtn') || 'Send Request',
    payload: {
      name,
      phone,
      email,
      message,
      token,
      booking
    },
    successMessage: result =>
      '✅ ' + (result.message || 'Request sent successfully! We will contact you soon.'),
    resetButton: {
      id: 'reset-form-btn',
      label: 'Send Another Request',
      onReset: () => {
        document.getElementById('costs-section').scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    onSuccess: () => {
      document.getElementById('contact-name').value = '';
      document.getElementById('contact-email').value = '';
      document.getElementById('contact-phone').value = '';
      document.getElementById('contact-message').value = '';

      const messageField = document.getElementById('contact-message');
      if (messageField) {
        messageField.dispatchEvent(new Event('input'));
      }

      resetTurnstile('catering-turnstile-container');
      document.getElementById('catering-turnstile-token').value = '';
    }
  });
}

// =========================
// INITIALIZE
// =========================
let calculatorInitialized = false;
let eventListenersAttached = false;

function initializeCostCalculator() {
  log('[Calculator] ========================================');
  log('[Calculator] Initializing cost calculator...');
  log('[Calculator] ========================================');

  const costsSection = document.getElementById('costs-section');
  if (!costsSection) {
    console.warn('[Calculator] Costs section not found, skipping init');
    return false;
  }

  log('[Calculator] Costs section found:', costsSection);
  log('[Calculator] Costs section display:', window.getComputedStyle(costsSection).display);
  log('[Calculator] Costs section visibility:', window.getComputedStyle(costsSection).visibility);

  try {
    const saved = localStorage.getItem('selectedLanguage');
    if (saved && saved !== langManager.getCurrentLanguage()) {
      langManager.setLanguage(saved);
    }
  } catch (err) {
    console.warn('[Calculator] Could not read selectedLanguage from localStorage', err);
  }

  // IMPORTANT: Remove old listeners before attaching new ones
  if (eventListenersAttached) {
    log('[Calculator] Removing old event listeners...');
    const oldSection = document.getElementById('costs-section');
    if (oldSection) {
      const newSection = oldSection.cloneNode(true);
      oldSection.parentNode.replaceChild(newSection, oldSection);
    }
    eventListenersAttached = false;
  }

  // Get fresh reference after potential clone
  const freshCostsSection = document.getElementById('costs-section');
  if (!freshCostsSection) {
    console.error('[Calculator] Lost costs section reference');
    return false;
  }

  enforceInputLimits();

  // Attach input listeners
  const galettesInput = freshCostsSection.querySelector('#galettes');
  const crepesInput = freshCostsSection.querySelector('#crepes');
  const durationInput = freshCostsSection.querySelector('#duration');

  log('[Calculator] Found inputs:', { galettesInput, crepesInput, durationInput });

  if (!galettesInput || !crepesInput || !durationInput) {
    console.error('[Calculator] Input fields not found');
    return false;
  }

  log('[Calculator] Attaching input listeners...');

  const handleInput = e => {
    log(`[Calculator] Input changed: ${e.target.id} = ${e.target.value}`);
    calculate();
  };

  galettesInput.addEventListener('input', handleInput);
  crepesInput.addEventListener('input', handleInput);
  durationInput.addEventListener('input', handleInput);

  const dateInput = freshCostsSection.querySelector('#event-date');
  if (dateInput) {
    dateInput.addEventListener('change', () => {
      log('[Calculator] Date changed');
      calculate();
    });

    const today = new Date().toISOString().split('T')[0];
    dateInput.setAttribute('min', today);
  }

  eventListenersAttached = true;
  log('[Calculator] Event listeners attached');

  // Initial calculation
  calculate();

  // Submit button
  const submitBtn = freshCostsSection.querySelector('#submit-booking');
  if (submitBtn) {
    const newSubmitBtn = submitBtn.cloneNode(true);
    submitBtn.parentNode.replaceChild(newSubmitBtn, submitBtn);
    newSubmitBtn.addEventListener('click', handleSubmit);
    log('[Calculator] Submit button wired up');
  }

  calculatorInitialized = true;

  log('[Calculator] ========================================');
  log('[Calculator] ✓ Cost calculator initialized successfully');
  log('[Calculator] ========================================');

  return true;
}

// =========================
// INITIALIZATION STRATEGY
// =========================

function tryInitialize() {
  log('[Calculator] tryInitialize called');
  const costsSection = document.getElementById('costs-section');

  if (!costsSection) {
    log('[Calculator] Costs section not found yet');
    return;
  }

  if (calculatorInitialized) {
    log('[Calculator] Already initialized, skipping');
    return;
  }

  log('[Calculator] DOM ready, initializing...');
  const success = initializeCostCalculator();
  if (!success) {
    console.error('[Calculator] Initialization failed');
  }
}

// STRATEGY 1: Listen for modals-ready event (works for both direct load and SPA)
window.addEventListener('modals-ready', event => {
  log('[Calculator] ======================================');
  log('[Calculator] modals-ready event received:', event.detail);
  log('[Calculator] ======================================');

  if (event.detail?.sceneName === 'catering') {
    // Reset state for fresh initialization
    calculatorInitialized = false;
    eventListenersAttached = false;

    log('[Calculator] Resetting init state for catering scene');

    // CRITICAL: Wait for CSS application AND force reflow
    const delay = event.detail.fromDirectLoad ? 50 : 500;
    log(`[Calculator] Waiting ${delay}ms for CSS to apply...`);

    setTimeout(() => {
      log('[Calculator] Delay complete, forcing reflow...');

      // Force a reflow before initialization to ensure styles are applied
      const section = document.getElementById('costs-section');
      if (section) {
        void section.offsetHeight; // Force reflow
        log('[Calculator] Reflow forced, section exists');
      } else {
        console.error('[Calculator] Section not found after delay!');
      }

      tryInitialize();
    }, delay);
  }
});

// STRATEGY 2: Handle initial page load before scene system starts
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    log('[Calculator] DOMContentLoaded fired');
    setTimeout(tryInitialize, 100);
  });
} else {
  // DOM already loaded
  log('[Calculator] DOM already loaded, trying immediate init');
  setTimeout(tryInitialize, 100);
}

// STRATEGY 3: Recalculation event for when modal reopens
window.addEventListener('recalculate-costs', () => {
  log('[Calculator] recalculate-costs event received');
  if (calculatorInitialized) {
    calculate();
  }
});

// Export for debugging
window.__calculatorDebug = {
  isInitialized: () => calculatorInitialized,
  hasListeners: () => eventListenersAttached,
  forceInit: () => {
    calculatorInitialized = false;
    eventListenersAttached = false;
    tryInitialize();
  }
};
